import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useListForm } from '../../hooks/useListForm';
import { useSceneUpdate } from '../../hooks/useSceneUpdate';
import { getAllScenes } from '../../utils/novelUtils';
import Conditionals from '../Conditionals';
import { getAllEndingsFromNovel } from '../../utils/endingsHelper';
import CustomSelect from '../CustomSelect';

const INITIAL_DATA = {
  name: '',
  prompt_ending_conditional: '',
  conditionals: [],
  next_scene: { scene_id: '', scene_type: 'static', priority: 0, conditionals: [] },
  states_trigger: { flags: [], scales: [] }
};

const RegularEndingsTab = ({ novelId, sceneId, scene, setScene, novel, loading, setLoading, setError, isProduction, sceneType = 'interactive' }) => {
  const { showForm, editingIndex, formData, setFormData, openAdd, openEdit, close } = useListForm(INITIAL_DATA);
  const { saveScene } = useSceneUpdate(novelId, sceneId, isProduction, setScene, setLoading, sceneType);
  const [sceneSearch, setSceneSearch] = useState('');

  const getNextSceneUrl = (sid, sceneType) => {
    const sceneTypePath = sceneType === 'interactive' ? 'interactive_scene' : sceneType === 'open_world' ? 'open_world_scene' : 'static_scene';
    const mode = isProduction ? 'production_change' : 'building';
    return `/${mode}/${novelId}/${sceneTypePath}/${sid}`;
  };

  const allScenes = getAllScenes(novel);
  const getAvailableScenes = (sceneType, searchTerm = '') =>
    allScenes.filter(s => s.type === sceneType && (s.id === formData.next_scene.scene_id || s.name.toLowerCase().includes(searchTerm.toLowerCase()))).sort((a, b) => a.name.localeCompare(b.name));
  const getSceneName = (sid, sceneType) => allScenes.find(s => s.id === sid && s.type === sceneType)?.name || 'Неизвестная сцена';

  const handleEditEndingClick = (ending, index) => {
    openEdit({
      name: ending.name,
      prompt_ending_conditional: ending.prompt_ending_conditional,
      conditionals: ending.conditionals || [],
      next_scene: ending.next_scene || { scene_id: '', scene_type: 'static' },
      states_trigger: ending.states_trigger || { flags: [], scales: [] }
    }, index);
    setSceneSearch('');
  };

  const handleAddEnding = () => { openAdd(); setSceneSearch(''); };

  const handleSaveEnding = async () => {
    if (!formData.name.trim() || !formData.prompt_ending_conditional.trim()) {
      setError('Заполните название и условие концовки');
      return;
    }
    setError(null);
    const updatedEndings = [...(scene?.endings || [])];
    const newEnding = {
      name: formData.name.trim(),
      prompt_ending_conditional: formData.prompt_ending_conditional.trim(),
      conditionals: formData.conditionals,
      next_scene: formData.next_scene.scene_id ? { scene_id: formData.next_scene.scene_id, scene_type: formData.next_scene.scene_type } : null,
      states_trigger: formData.states_trigger
    };
    if (editingIndex !== null) {
      updatedEndings[editingIndex] = { ...updatedEndings[editingIndex], ...newEnding };
    } else {
      updatedEndings.push(newEnding);
    }
    const success = await saveScene({ ...scene, endings: updatedEndings }, 'Концовка успешно сохранена!');
    if (success) close();
  };

  const handleDeleteEnding = async (index) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту концовку?')) return;
    const updatedEndings = [...(scene?.endings || [])];
    updatedEndings.splice(index, 1);
    await saveScene({ ...scene, endings: updatedEndings }, 'Концовка успешно удалена!');
  };

  const handleCancelEndingForm = () => { close(); setSceneSearch(''); setError(null); };

  const updateFormFlags = (flags) => setFormData(prev => ({ ...prev, states_trigger: { ...prev.states_trigger, flags } }));
  const updateFormScales = (scales) => setFormData(prev => ({ ...prev, states_trigger: { ...prev.states_trigger, scales } }));

  return (
    <div className="isend-tab-content">
      <p className="isend-help">Настройте различные способы завершения этой интерактивной сцены и что происходит дальше</p>

      <div className="isend-add-wrap">
        <button className="isend-add-btn" onClick={handleAddEnding} disabled={loading}>
          <span className="button-icon">➕</span>
          Добавить концовку
        </button>
      </div>

      <div className="isend-list">
        {scene?.endings && scene.endings.length > 0 ? (
          <div className="isend-grid">
            {scene.endings.map((ending, index) => (
              <div key={ending.id || index} className="isend-card">
                <div className="isend-card-head">
                  <h4 className="isend-card-name">{ending.name}</h4>
                </div>
                <div className="isend-card-actions">
                  {ending.next_scene && (
                    <Link className="isend-btn-link" to={getNextSceneUrl(ending.next_scene.scene_id, ending.next_scene.scene_type)}>
                      Перейти к сцене
                    </Link>
                  )}
                  <button className="isend-btn-edit" onClick={() => handleEditEndingClick(ending, index)} disabled={loading}>Редактировать концовку</button>
                  <button className="isend-btn-delete" onClick={() => handleDeleteEnding(index)} disabled={loading}>Удалить</button>
                </div>
                <div className="isend-card-body">
                  <div className="isend-details">
                    <p className="prompt-description">{ending.prompt_ending_conditional}</p>
                    {ending.next_scene ? (
                      <p><strong>Следующая сцена:</strong>{' '}
                        <Link to={getNextSceneUrl(ending.next_scene.scene_id, ending.next_scene.scene_type)}>
                          {getSceneName(ending.next_scene.scene_id, ending.next_scene.scene_type)}
                        </Link>
                        {' '}({ending.next_scene.scene_type})
                      </p>
                    ) : (
                      <p><strong>Следующая сцена:</strong> Нет следующей сцены (конец истории)</p>
                    )}
                    {ending.conditionals && ending.conditionals.length > 0 && (
                      <p><strong>Условия:</strong> {ending.conditionals.length}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="isend-empty"><p>Для этой сцены пока не настроены концовки.</p></div>
        )}
      </div>

      {showForm && (
        <div className="isend-overlay">
          <div className="isend-modal">
            <h3 className="isend-modal-title">{editingIndex !== null ? 'Редактировать концовку' : 'Добавить концовку'}</h3>

            <div className="isend-form-group">
              <label className="isend-label">Название концовки:</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="isend-input" disabled={loading} placeholder="Введите название концовки..." required />
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Условие концовки:</label>
              <textarea value={formData.prompt_ending_conditional}
                onChange={(e) => setFormData({ ...formData, prompt_ending_conditional: e.target.value })}
                className="isend-textarea" disabled={loading} rows={3}
                placeholder="Опишите, когда эта концовка должна срабатывать..." required />
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Условия:</label>
              <Conditionals
                flags={novel?.states?.flags || []} scales={novel?.states?.scales || []}
                endings={getAllEndingsFromNovel(novel)} value={formData.conditionals}
                onChange={(conditionals) => setFormData({ ...formData, conditionals })} disabled={loading} />
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Триггер состояний:</label>
              <p className="isend-form-help">Настройте, какие флаги и весы изменить при достижении этой концовки</p>

              <div className="isend-trigger-section">
                <h5>Триггер флага:</h5>
                {formData.states_trigger.flags.map((flagTrigger, index) => (
                  <div key={index} className="isend-trigger-row">
                    <CustomSelect value={flagTrigger.flag_id || ''} onChange={(e) => { const f = [...formData.states_trigger.flags]; f[index] = { ...f[index], flag_id: e.target.value }; updateFormFlags(f); }} className="app-select" disabled={loading}>
                      <option value="">Флаг...</option>
                      {(novel?.states?.flags || []).map((flag) => <option key={flag.id} value={flag.id}>{flag.name}</option>)}
                    </CustomSelect>
                    <CustomSelect value={flagTrigger.value !== undefined ? flagTrigger.value.toString() : 'true'} onChange={(e) => { const f = [...formData.states_trigger.flags]; f[index] = { ...f[index], value: e.target.value === 'true' }; updateFormFlags(f); }} className="isend-input" disabled={loading}>
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </CustomSelect>
                    <button type="button" onClick={() => updateFormFlags(formData.states_trigger.flags.filter((_, i) => i !== index))} className="isend-btn-cancel isend-trigger-remove" disabled={loading}>Удалить</button>
                  </div>
                ))}
                <button type="button" onClick={() => updateFormFlags([...formData.states_trigger.flags, { flag_id: '', value: true }])} className="isend-trigger-add" disabled={loading}>Добавить триггер флага</button>
              </div>

              <div className="isend-trigger-section">
                <h5>Триггер веса:</h5>
                {formData.states_trigger.scales.map((scaleTrigger, index) => (
                  <div key={index} className="isend-trigger-row">
                    <CustomSelect value={scaleTrigger.scale_id || ''} onChange={(e) => { const s = [...formData.states_trigger.scales]; s[index] = { ...s[index], scale_id: e.target.value }; updateFormScales(s); }} className="isend-input" disabled={loading}>
                      <option value="">Вес...</option>
                      {(novel?.states?.scales || []).map((scale) => <option key={scale.id} value={scale.id}>{scale.name}</option>)}
                    </CustomSelect>
                    <input type="text" value={scaleTrigger.change || ''} onChange={(e) => { const s = [...formData.states_trigger.scales]; s[index] = { ...s[index], change: e.target.value }; updateFormScales(s); }} placeholder="Используйте +3, -5, или 3 (для абсолютного значения)" className="isend-input" disabled={loading} />
                    <button type="button" onClick={() => updateFormScales(formData.states_trigger.scales.filter((_, i) => i !== index))} className="isend-btn-cancel isend-trigger-remove" disabled={loading}>Удалить</button>
                  </div>
                ))}
                <button type="button" onClick={() => updateFormScales([...formData.states_trigger.scales, { scale_id: '', change: '' }])} className="isend-trigger-add" disabled={loading}>Добавить триггер веса</button>
              </div>
            </div>

            <div className="isend-form-group">
              <label className="isend-label">Следующая сцена:</label>
              <div className="isend-next-scene">
                <div className="isend-form-group">
                  <label className="isend-label">Тип сцены:</label>
                  <CustomSelect value={formData.next_scene.scene_type} onChange={(e) => setFormData({ ...formData, next_scene: { ...formData.next_scene, scene_type: e.target.value, scene_id: '' } })} className="app-select" disabled={loading}>
                    <option value="static">Статическая</option>
                    <option value="interactive">Интерактивная</option>
                    <option value="open_world">Открытый мир</option>
                  </CustomSelect>
                </div>
                <div className="isend-form-group">
                  <label className="isend-label">Выбор сцены:</label>
                  <input type="text" placeholder="Поиск сцен..." value={sceneSearch} onChange={(e) => setSceneSearch(e.target.value)} className="isend-input" style={{marginBottom: '10px'}} />
                  <CustomSelect value={formData.next_scene.scene_id} onChange={(e) => setFormData({ ...formData, next_scene: { ...formData.next_scene, scene_id: e.target.value } })} className="app-select" disabled={loading}>
                    <option value="">Нет следующей сцены (конец истории)</option>
                    {getAvailableScenes(formData.next_scene.scene_type, sceneSearch).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </CustomSelect>
                </div>
              </div>
            </div>

            <div className="isend-form-actions">
              <button type="button" onClick={handleSaveEnding} disabled={loading} className="isend-btn-primary">{loading ? 'Загрузка...' : 'Сохранить'}</button>
              <button type="button" onClick={handleCancelEndingForm} disabled={loading} className="isend-btn-cancel">Отменить</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegularEndingsTab;
